/**
 * The review module's server state: the WAITING_HUMAN queue --
 * elements no classification tier could confidently resolve
 * (classification IS NULL is the escalation signal, queried, never
 * bookkept) -- and the override that answers one.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface PendingClassification {
  element_id: number;
  project_id: string;
  origin_id: string;
  element_name: string | null;
  ifc_class: string | null;
  object_type: string | null;
}

/** The four SystemTypes; NULL is the only fallback, and a human
 * answer is always one of these (ARCHITECTURAL covers non-MEP). */
export const CATEGORIES = ['ELECTRICAL', 'MECHANICAL', 'PLUMBING', 'ARCHITECTURAL'] as const;

export function usePendingClassifications(projectId: string) {
  return useQuery<PendingClassification[]>({
    queryKey: ['classification-pending', projectId],
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/classification/pending?project_id=${encodeURIComponent(projectId)}`,
      );
      if (!res.ok) throw new Error(`pending classifications: ${res.status}`);
      return (await res.json()) ?? [];
    },
  });
}

export function useOverrideClassification(projectId: string, signedBy: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      elementId,
      category,
    }: {
      elementId: number;
      category: (typeof CATEGORIES)[number];
    }) => {
      const res = await authFetch(`${API_BASE}/v1/classification/${elementId}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, signed_by: signedBy }),
      });
      if (!res.ok) throw new Error(`override: ${res.status}`);
      return res.json();
    },
    // The worker applies the override (flywheel save + propagation);
    // refetch shows the queue shrinking as answers land.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['classification-pending', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['elements', projectId] });
    },
  });
}
